import { CommonModule } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, signal, viewChild, ViewEncapsulation } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { FieldType, FormlyModule } from "@ngx-formly/core";
import { TranslateModule } from "@ngx-translate/core";
import { EmailConnectionType } from '@phx-erp/shared';
import { I18nService } from "../../../@core/services/i18n/i18n.service";
import { PhxSelect } from "../../../shared/components/phx-select/phx-select.component";

@Component({
    selector: "app-formly-field-email-connection-type",
    templateUrl: "./formly-field-email-connection-type.component.html",
    styleUrl: "./formly-field-email-connection-type.component.scss",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, FormlyModule, ReactiveFormsModule, PhxSelect, TranslateModule]
})
export class FormlyFieldEmailConnectionTypeComponent extends FieldType implements AfterViewInit {
    destroyRef = inject(DestroyRef);
    private cdRef = inject(ChangeDetectorRef);
    private i18n = inject(I18nService);
    selectComponent = viewChild<PhxSelect<any>>("selectComponent");
    connectionTypeArray = signal<{ label: string; value: EmailConnectionType }[]>([]);
    allConnectionTypes = signal<{ label: string; value: EmailConnectionType }[]>([]);

    constructor() {
        super();
        // Auto-generate from enum using translations - automatically includes new types!
        const allTypes = this.generateConnectionTypes();
        this.allConnectionTypes.set(allTypes);
        this.connectionTypeArray.set(allTypes);
    }

    /**
     * Generate connection types array from EmailConnectionType enum
     * Uses translations from i18n files (en.json, de.json, etc.)
     * Translation key pattern: enums.SMTP, enums.OFFICE365_MULTI_TENANT, etc.
     * Automatically includes all enum values - no manual updates needed!
     */
    private generateConnectionTypes(): { label: string; value: EmailConnectionType }[] {
        return Object.values(EmailConnectionType).map(value => ({
            label: this.i18n.translate(_(`enums.${value}`)),
            value: value as EmailConnectionType
        }));
    }

    ngAfterViewInit(): void {
        // Ensure form control value changes are properly propagated
        if (this.formControl) {
            this.formControl.valueChanges
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    // Trigger change detection to ensure parent component sees the change
                    this.cdRef.markForCheck();
                });
        }
    }

    /**
     * Filter connection types by search term
     * Searches in both the translated label and the enum value
     * @param term - The search term entered by the user
     */
    onFilter(term: string): void {
        if (!term || term.length === 0) {
            this.connectionTypeArray.set(this.allConnectionTypes());
        } else {
            const searchTerm = term.toLowerCase();
            const filtered = this.allConnectionTypes().filter(option =>
                option.label.toLowerCase().indexOf(searchTerm) > -1 ||
                option.value.toLowerCase().indexOf(searchTerm) > -1
            );
            this.connectionTypeArray.set(filtered);
        }
        this.selectComponent()?.loading.set(false);
        this.cdRef.markForCheck();
    }
}

